import { FC, PropsWithChildren, useCallback, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { ErrorOption } from 'react-hook-form/dist/types/errors';

import { get, isEmpty } from 'lodash-es';

import { isValidGolfId } from '../../helpers/regex';
import useTranslate from '../../hooks/useTranslate';
import { useGolfIdVerification, useProfile } from '../../services/apis/profile/api';
import { GolfIdVerificationFormFieldValues } from './types';

const GolfIdVerificationFormProvider: FC<PropsWithChildren> = props => {
  const { children } = props;
  const { translate } = useTranslate();
  const { data: profile } = useProfile();
  const { mutateAsync: verifyGolfId } = useGolfIdVerification();
  const methods = useForm<GolfIdVerificationFormFieldValues>({
    resolver: (values, context, options) => {
      const errorsMap = new Map<keyof GolfIdVerificationFormFieldValues, ErrorOption>();

      if (!isValidGolfId(values.golfId.trim())) {
        errorsMap.set('golfId', {
          type: 'client',
          message: String(translate('GolfIdVerification.errors.golfId.required')),
        });
      }

      if (isEmpty(values.password.trim())) {
        errorsMap.set('password', {
          type: 'client',
          message: String(translate('GolfIdVerification.errors.password.required')),
        });
      }

      return {
        errors: Object.fromEntries(errorsMap),
        values,
      };
    },
    defaultValues: {
      golfId: '',
      password: '',
    },
  });
  const { reset, handleSubmit, setError, setValue } = methods;

  const onSubmit = useCallback<SubmitHandler<GolfIdVerificationFormFieldValues>>(
    async data => {
      try {
        await verifyGolfId(data);

        reset();
      } catch (err) {
        setError('root', {
          type: 'server',
          message: String(get(err, 'message') || err),
        });
      }
    },
    [reset, setError, verifyGolfId]
  );

  useEffect(() => {
    if (profile?.golfId) {
      setValue('golfId', profile?.golfId);
    }
  }, [profile?.golfId, setValue]);

  return (
    <FormProvider {...methods} handleSubmit={() => handleSubmit(onSubmit)}>
      {children}
    </FormProvider>
  );
};

export default GolfIdVerificationFormProvider;
